import { isDevEnvironment } from '../utils/utils';
import store from 'store/main';
import blAuth from './bl-auth/bl-auth';
import { isObject } from 'underscore';
import { API_HOST, METRICS_HOST, TRANSLATIONS_SERVICE_HOST } from '@/utils/connections/endpoints';
import { getApexDomain } from '@/utils/connections/get-apex-domain';

// opt-in retry budget for calls worth retrying on a dropped connection - 4 retries is ~30s of backoff
export const NETWORK_RETRIES = 4;

const tempChannel: Record<string, number | undefined> = {
	value: undefined,
};

export const resetTempChannel = () => {
	tempChannel.value = undefined;
};

export const setTempChannel = (channel: number) => {
	tempChannel.value = channel;
};

export const handleFailedRequestWithMessage = async (res: Response) => {
	const cloned = res.clone();
	if ('json' in cloned) {
		try {
			const json = await cloned.json();
			const message = json?.error;
			if (message && typeof message === 'string') {
				return message;
			}
		} catch (e) {
			console.error(e);
		}
	}
	const clonedTwo = res.clone();
	if ('text' in clonedTwo) {
		try {
			const text = await clonedTwo.text();
			return text;
		} catch (e) {
			console.error(e);
		}
	}

	return cloned.statusText;
};

export const fetchFailTimeout = async (attempt: number) => {
	const interval = Math.pow(2, attempt) * 1000;
	await new Promise(r => setTimeout(r, interval));
};

// fetch rejects with a TypeError on network failure - the message differs per browser
export const isNetworkError = (e: unknown): boolean => e instanceof TypeError;

export const isSameOrigin = () => {
	return [
		getApexDomain(import.meta.env.VITE_URL ?? ''),
		import.meta.env.VITE_BRAND_TV_DOMAIN,
		import.meta.env.VITE_WEBINAR_PLUS_DOMAIN
	].includes(getApexDomain(location.hostname));
};

/*


New modes here:
- In the old system (non unified auth) the user's token would be passed
as an argument to the fetch function and included as the `bl-token` header

- In the new system there are two modes, same-origin and cross-origin

- In same-origin the token should be included as a cookie which will be automatically
appended to the request because `credentials: 'include'` is set

- In cross-origin the token should be included as a bearer token in the `Authorization` header
This is primarily used for development when the app may be running on localhost 
and the API is on a different domain


*/
const getHeaders = (headers = {}, withChannel?: number): Record<string, string> => {
	const withTempChannel = tempChannel.value || withChannel ? String(tempChannel.value || withChannel) : undefined;
	if (isSameOrigin() || !blAuth.accessToken) {
		// ignore the provided token - this is a same-origin request
		// and will include the token in the cookie
		return {
			"Content-Type": "application/json",
			...(withTempChannel ? { 'bl-temp-channel': withTempChannel } : {}),
			...headers
		};
	} else {
		return {
			"Content-Type": "application/json",
			"bl-auth-method": "bearer",
			"Authorization": `Bearer ${blAuth.accessToken}`,
			...(withTempChannel ? { 'bl-temp-channel': withTempChannel } : {}),
			...headers,
		};
	}
};

function getParams(headers = {}, abortController?: AbortController, withChannel?: number) {
	const requestInit = {
		method: "GET",
		headers: getHeaders(headers, withChannel),
		credentials: isSameOrigin() ? "include" as RequestCredentials : undefined,
		signal: abortController?.signal
	};

	return requestInit;
}

function postParams(data: any, headers: { [key: string]: string }, withChannel?: number) {
	return {
		method: "POST",
		headers: getHeaders(headers, withChannel),
		credentials: isSameOrigin() ? "include" as RequestCredentials : undefined,
		body: JSON.stringify(data)
	};
}

function patchParams(data: any, headers: { [key: string]: string }, withChannel?: number) {
	return {
		method: "PATCH",
		headers: getHeaders(headers, withChannel),
		credentials: isSameOrigin() ? "include" as RequestCredentials : undefined,
		body: JSON.stringify(data)
	};
}

function putParams(data: any, headers = {}, withChannel?: number) {
	return {
		method: "PUT",
		headers: getHeaders(headers, withChannel),
		credentials: isSameOrigin() ? "include" as RequestCredentials : undefined,
		body: JSON.stringify(data)
	};
}

function deleteParams(data?: any, headers = {}, withChannel?: number) {
	return {
		method: "DELETE",
		headers: getHeaders(headers, withChannel),
		credentials: isSameOrigin() ? "include" as RequestCredentials : undefined,
		body: data ? JSON.stringify(data) : null
	};
}

function getHvParams(token?: string) {
	return {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? ""
		}
	};
}

function getHvParamsWithHeaders(headers: { [key: string]: string } = {}, credentials?: boolean) {
	return {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			...headers
		},
		credentials: credentials ? 'include' as RequestCredentials : undefined
	};
}

function postHvParams(data: any, token?: string, headers = {}) {
	return {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? "",
			...headers,
		},
		body: JSON.stringify(data)
	};
}

function putHvParams(data: any, token?: string) {
	return {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? ""
		},
		body: JSON.stringify(data)
	};
}

function putHvParamsWithHeaders(data: any, headers: { [key: string]: string }, token?: string) {
	return {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? "",
			...headers
		},
		body: JSON.stringify(data)
	};
}

function postHvParamsWithHeaders(data: any, headers: { [key: string]: string }, token?: string) {
	return {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? "",
			...headers
		},
		body: JSON.stringify(data)
	};
}

function deleteHvParams(data?: any, token?: string) {
	return {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? ""
		},
		body: JSON.stringify(data)
	};
}

function patchHvParams(data: any, token?: string) {
	return {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			"Bl-Profile-Token": token ?? ""
		},
		body: JSON.stringify(data)
	};
}

function parsePath(path: string) {
	return `${API_HOST}${path}`;
}

function parsePathMetrics(path: string) {
	return `${METRICS_HOST}${path}`;
}

export function getGreenroomEnvironmentUrl() {
	if (import.meta.env.VITE_STAGE) {
		switch (import.meta.env.VITE_STAGE) {
			case 'staging':
			case 'qa': {
				return `live-${import.meta.env.VITE_STAGE}.com`;
			}
			case 'local':
			case 'sandbox':
			case 'dev': {
				return 'live-dev.com';
			}
			case 'prod': {
				return '.live';
			}
			case 'hotfix': {
				return `live-hot.com`;
			}
		}
	} else if (isDevEnvironment()) {
		return 'live-dev.com';
	}
}

function parseGreenroomPath(path: string, isGreenroomSync?: boolean) {
	return isGreenroomSync ? `${import.meta.env.VITE_GREENROOM_APP_SYNC_HOST}${path}` : `${import.meta.env.VITE_GREENROOM_API_HOST}${path}`;
}

export const HvHostMap = {
	authorization: String(import.meta.env.VITE_HOSTS_AUTHORIZATION),
	chat: String(import.meta.env.VITE_HOSTS_CHAT),
	datalake: String(import.meta.env.VITE_HOSTS_DATALAKE),
	eventData: String(import.meta.env.VITE_HOSTS_EVENT_DATA),
	registration: String(import.meta.env.VITE_HOSTS_REGISTRATION),
	imageResizer: String(import.meta.env.VITE_HOSTS_IMAGE_RESIZER),
	sessionTracking: String(import.meta.env.VITE_HOSTS_SESSION_TRACKING),
	streamSso: String(import.meta.env.VITE_HOSTS_STREAM_SSO),
	stripe: String(import.meta.env.VITE_HOSTS_STRIPE),
	surveys: String(import.meta.env.VITE_HOSTS_SURVEYS),
	transcripts: String(import.meta.env.VITE_HOSTS_TRANSCRIPTS || import.meta.env.VITE_HV_HOST)
};

type HvKey = keyof typeof HvHostMap;
type HvHost = typeof HvHostMap[HvKey];
export const parseHvPath = (host: HvHost, path: string) => {
	return `${host}${path}`;
};

export function parseTranslationsServicePath(path: string) {
	return `${TRANSLATIONS_SERVICE_HOST}${path}`;
}

export class FetchError {
	status: number;
	error: string;
	constructor(err: { status: number, error: string }) {
		this.status = err.status;
		this.error = err.error;
	}

	toString() {
		return `${this.status}: ${this.error}`;
	}
}

// The message the API sent back on a 4xx - those are written for the admin to read. Anything else (a
// 5xx, or a statusText we filled in ourselves) is internal, so the caller's own copy is used instead.
export const getFetchErrorMessage = (error: unknown, fallback: string): string => {
	if (error instanceof FetchError && error.status >= 400 && error.status < 500 && error.error) {
		return error.error;
	}

	return fallback;
};

const returnJson = async <ReturnType>(res: Response, allowText?: boolean): Promise<ReturnType> => {
	// content-type header might include a charset - just check that it begins with application/json
	if (res.headers.get('content-type')?.startsWith('application/json')) {
		const json = await res.json();

		if (res.headers.has('bl-temp-channel')) {
			tempChannel.value = Number(res.headers.get('bl-temp-channel'));
		}

		if (res.ok) {
			return json as ReturnType;
			// fetch requests do not natively throw on non-2XX status codes. Throw our own error.
		} else {
			if (isObject(json) && 'error' in json && json.error) {
				throw new FetchError({
					status: res.status,
					error: json.error
				});
			} else {
				throw new FetchError({
					status: res.status,
					error: res.statusText
				});
			}
		}
	} else {
		const text = await res.text();

		if (allowText && res.ok) {
			return { response: text } as unknown as ReturnType;
		}

		// since this is supposed to receive json and it did not, get the response as text and throw that as an error message
		throw new FetchError({
			status: res.status,
			error: text
		});
	}
};

const returnAnyJson = async (res: Response): Promise<any> => {
	return returnJson(res, true);
};

const returnOk = async (res: Response): Promise<boolean> => {
	return res.ok;
};

export type GetJsonPayload = {
	path: string | URL,
	/** @deprecated */
	token?: string,
	headers?: { [key: string]: string },
	isGreenroom?: boolean,
	metrics?: boolean,
	abortController?: AbortController,
	withChannel?: number
};

// get has too many args and is annoying
export const GetJson = async <ReturnType = Record<string, unknown>>(payload: GetJsonPayload): Promise<ReturnType> => {
	const { path, headers, isGreenroom, abortController, withChannel } = payload;
	const pathParser = payload.metrics ? parsePathMetrics : isGreenroom ? parseGreenroomPath : parsePath;
	const _path = path instanceof URL ? path.href : await pathParser(path);

	try {
		const res = await fetch(_path, getParams(headers, abortController, withChannel));
		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			// threw before the request could properly be sent - this is a code error
			console.error(e);
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

export const Get = async (
	path: string,
	/** @deprecated */
	token?: string,
	headers?: { [key: string]: string },
	returnRaw?: boolean,
	isGreenroom?: boolean,
	isGreenroomSync?: boolean,
	retries = 0
): Promise<any> => {
	let _path;
	if (isGreenroom) {
		_path = await parseGreenroomPath(path, isGreenroomSync);
	} else {
		_path = await parsePath(path);
	}

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, getParams(headers));
			if (returnRaw) {
				return res;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

export const GetMetrics = async (
	path: string,
	/** @deprecated */
	token?: string,
	headers?: { [key: string]: string },
	returnRaw?: boolean,
	isGreenroom?: boolean,
	abortController?: AbortController,
	retries = 0
): Promise<any> => {
	let _path;
	if (isGreenroom) {
		_path = await parseGreenroomPath(path);
	} else {
		_path = await parsePathMetrics(path);
	}

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, getParams(headers, abortController));
			if (returnRaw) {
				return res;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

/**
 * @remarks Token might be optional for end user code - uses different header for token
 * @param path Path to lambda function
 * @param token Optional for End User
 * @returns any
 */
export const GetHv = async <T = any>(service: HvHost, path: string, token?: string): Promise<T> => {
	const _path = parseHvPath(service, path);

	try {
		const res = await fetch(_path, getHvParams(token));
		return returnAnyJson(res);
	} catch (e: unknown) {
		// failure throws TypeError
		if (e instanceof TypeError) {
			console.error(path, e.message);
			throw e;
		}

		throw new Error(`Unable to fetch ${path}: ${e}`);
	}
};

export type GetJsonHvPayload = {
	path: string,
	headers?: { [key: string]: string },
	service: HvHost,
	includeCredentials?: boolean
};

// get has too many args and is annoying
export const GetHvJson = async <ReturnType = Record<string, unknown>>(payload: GetJsonHvPayload): Promise<ReturnType> => {
	const { path, service, headers } = payload;
	const _path = parseHvPath(service, path);

	try {
		const res = await fetch(_path, getHvParamsWithHeaders(headers, payload.includeCredentials));
		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			// threw before the request could properly be sent - this is a code error
			console.error(e);
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

export type PutPostJsonHvPayload = {
	path: string,
	headers?: { [key: string]: string },
	service: HvHost,
	data: any,
	allowText?: boolean,
	blProfileToken?: string
};

export const PutHvJson = async <ReturnType = Record<string, unknown>>(payload: PutPostJsonHvPayload): Promise<ReturnType> => {
	const { path, service, headers, data, blProfileToken, allowText } = payload;
	const _path = parseHvPath(service, path);

	try {
		const res = await fetch(_path, putHvParamsWithHeaders(data, headers ?? {}, blProfileToken));
		return await returnJson<ReturnType>(res, allowText);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			// threw before the request could properly be sent - this is a code error
			console.error(e);
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

export const PostHvJson = async <ReturnType = Record<string, unknown>>(payload: PutPostJsonHvPayload): Promise<ReturnType> => {
	const { path, service, headers, data, blProfileToken, allowText } = payload;
	const _path = parseHvPath(service, path);

	try {
		const res = await fetch(_path, postHvParamsWithHeaders(data, headers ?? {}, blProfileToken));
		return await returnJson<ReturnType>(res, allowText);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			// threw before the request could properly be sent - this is a code error
			console.error(e);
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

export const GetTranslationService = async (
	path: string,
	/** @deprecated */
	token?: string,
	headers?: { [key: string]: string },
	retries = 0
): Promise<any> => {
	const _path = await parseTranslationsServicePath(path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, getParams(headers));

			return res;
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

type AnySerializable = Record<string, unknown> | Record<string, unknown>[];

export const PutTranslationService = async (
	path: string,
	/** @deprecated */
	token?: string,
	body: AnySerializable = {}
): Promise<boolean> => {
	const _path = await parseTranslationsServicePath(path);

	try {
		const res = await fetch(_path, putParams(body));
		return await returnOk(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

/** @deprecated use PatchJson instead */
export const Patch = async <T>(
	path: string,
	/** @deprecated */
	token: string | undefined,
	data: T,
	returnResults?: boolean,
	returnRaw?: boolean,
	retries = 0
): Promise<any> => {
	const _path = await parsePath(path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, patchParams(data, {}));

			if (returnRaw) {
				return res;
			}

			if (!returnResults) {
				return res.ok;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

// any type is appropriate for a newly added generic not adopted anywhere
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PatchJson = async <InputType, ReturnType>(payload: PostJsonPayload<InputType>): Promise<ReturnType> => {
	const { path, data, headers, isGreenroom, withChannel } = payload;
	const _path = path instanceof URL ? path.href : isGreenroom ? await parseGreenroomPath(path) : await parsePath(path);

	try {
		const res = await fetch(_path, patchParams(data, headers ?? {}, withChannel));
		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

export type PutJsonPayload<T> = {
	path: string | URL,
	/** @deprecated */
	token?: string,
	data: T,
	returnResults?: boolean,
	returnRaw?: boolean,
	returnErrorMsg?: boolean,
	headers?: Record<string, string>,
	withChannel?: number,
}

// any type is appropriate for a newly added generic not adopted anywhere
export const PutJson = async <InputType, ReturnType>(payload: PutJsonPayload<InputType>): Promise<ReturnType> => {
	const { path, data, headers } = payload;
	const _path = path instanceof URL ? path.href : await parsePath(path);

	try {
		const res = await fetch(_path, putParams(data, headers));
		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

// any type is appropriate for a newly added generic not adopted anywhere
/** @deprecated migrate to PutJson instead */
export const Put = async <T>(
	path: string,
	/** @deprecated token is no longer required */
	token: string | undefined,
	data: T,
	returnResults?: boolean,
	returnRaw?: boolean,
	returnErrorMsg?: boolean,
	headers?: Record<string, string>,
	isGreenroom?: boolean,
	isGreenroomSync?: boolean,
	retries = 0
): Promise<any> => {
	let _path;
	if (isGreenroom) {
		_path = await parseGreenroomPath(path, isGreenroomSync);
	} else {
		_path = await parsePath(path);
	}

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, putParams(data, headers));

			if (returnErrorMsg && !res.ok) {
				const result = await handleFailedRequestWithMessage(res);
				return Promise.reject(result);
			}


			if (returnRaw) {
				return res;
			}

			if (!returnResults) {
				return res.ok;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

/**
 * @remarks Token might be optional for end user code - uses different header for token
 * @param path Path at AWS
 * @param data Any js object
 * @param token Technically optional, but might return failure by API side
 * @returns Promise either JSON or boolean depending on returnResults
 */
export const PutHv = async <T>(service: HvHost, path: string, data: T, token?: string, retries = 0): Promise<any> => {
	const _path = parseHvPath(service, path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, putHvParams(data, token));

			return await res.json();
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

export const PatchHv = async <T>(service: HvHost, path: string, data: T, token?: string, noReturn?: boolean, returnRaw?: boolean, retries = 0): Promise<any> => {
	const _path = parseHvPath(service, path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res: Response = await fetch(_path, patchHvParams(data, token));

			if (noReturn) {
				return res.ok;
			}

			if (returnRaw) {
				return res;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

export const PostAbortable = <T>(
	path: string,
	/** @deprecated */
	token: string | undefined,
	data: T,
	noReturn?: boolean,
	headers = {}
): [AbortController, Promise<any>] => {
	const controller = new AbortController();
	const signal = controller.signal;

	return [
		controller,
		fetch(parsePath(path), { ...postParams(data, headers), signal }).then(res => res.json()),
	];
};

export type PostJsonPayload<T> = {
	path: string | URL,
	token?: string,
	data: T,
	headers?: Record<string, string>,
	isGreenroom?: boolean,
	withChannel?: number,
}

// any type is appropriate for a newly added generic not adopted anywhere
export const PostJson = async <InputType, ReturnType>(payload: PostJsonPayload<InputType>): Promise<ReturnType> => {
	const { path, data, headers, isGreenroom, withChannel } = payload;
	const _path = path instanceof URL ? path.href : isGreenroom ? await parseGreenroomPath(path) : await parsePath(path);

	try {
		const res = await fetch(_path, postParams(data, headers ?? {}, withChannel));
		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};

// any type is appropriate for a newly added generic not adopted anywhere
/** @deprecated use PostJson instead */
export const Post = async <T>(
	path: string,
	/** @deprecated */
	token: string | undefined,
	data: T,
	noReturn?: boolean,
	headers = {},
	returnRaw?: boolean,
	returnErrorMsg?: boolean,
	isGreenroom?: boolean,
	retries = 0
): Promise<any> => {
	let _path;
	if (isGreenroom) {
		_path = await parseGreenroomPath(path);
	} else {
		_path = await parsePath(path);
	}

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, postParams(data, headers));

			if (returnErrorMsg && !res.ok) {
				const result = await handleFailedRequestWithMessage(res);
				return Promise.reject(result);
			}

			if (noReturn) {
				return res.ok;
			}

			if (returnRaw) {
				return res;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

/**
 * @remarks Token might be optional for end user code - uses different header for token
 * @param path Path at AWS
 * @param data Any js object
 * @param token Technically optional, but might return failure by API side
 * @param noReturn If true will resolve boolean
 * @param headers Additional headers to pass into therequest
 * @returns Promise either JSON or boolean depending on noReturn
 */
export const PostHv = async <T>(service: HvHost, path: string, data: T, token?: string, noReturn?: boolean, headers?: Record<string, string>, retries = 0): Promise<any> => {
	const _path = parseHvPath(service, path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, postHvParams(data, token, headers));

			if (!res.ok) {
				if (noReturn) {
					return false;
				}

				if (res.headers.get('content-type')?.includes('application/json')) {
					const err = await res.json();
					throw new FetchError({ status: res.status, error: err.error || err.reason || err.message });
				} else {
					const err = await res.text();
					throw new FetchError({ status: res.status, error: err });
				}
			}

			return noReturn ? (res.ok) : await res.json();
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

/**
 * @remarks No token needed for posting analytics data
 * @param path Path at AWS
 * @param data Any js object
 * @returns Promise boolean -> user agent successfully queued data
 */
export const Beacon = <T>(service: HvHost, path: string, data: T): boolean => {
	const _path = parseHvPath(service, path);

	return navigator.sendBeacon(_path, JSON.stringify(data));
};

/**
 * @remarks Token might be optional for end user code - uses different header for token
 * @param path Path at AWS
 * @param data Any js object
 * @param token Technically optional, but might return failure by API side
 * @returns Promise either JSON or boolean depending on returnResults
 */
export const DeleteHv = async <T>(service: HvHost, path: string, data?: T, token?: string, retries = 0): Promise<any> => {
	const _path = parseHvPath(service, path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, deleteHvParams(data, token));

			return res.ok;
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

/** @deprecated use DeleteJson instead */
export const Delete = async <T>(
	path: string,
	token?: string | undefined,
	data?: T,
	returningDeleted?: boolean,
	returnRaw?: boolean,
	retries = 0
): Promise<any> => {
	const _path = await parsePath(path);

	let attempt = 0;
	for (let i = 0; i <= retries; ++i) {
		try {
			const res = await fetch(_path, deleteParams(data));

			if (returnRaw) {
				return res;
			}

			if (!returningDeleted) {
				return res.ok;
			}

			return returnAnyJson(res);
		} catch (e: any) {
			if (isNetworkError(e) && i < retries) {
				await fetchFailTimeout(++attempt);
				continue;
			}

			throw e;
		}
	}
};

export type DeleteJsonPayload<T> = {
	path: string | URL,
	/** @deprecated */
	token?: string,
	data: T,
	returnResults?: boolean,
	returnRaw?: boolean,
	returnErrorMsg?: boolean,
	headers?: Record<string, string>,
	withChannel?: number,
}

// any type is appropriate for a newly added generic not adopted anywhere
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DeleteJson = async <InputType, ReturnType>(payload: DeleteJsonPayload<InputType>): Promise<ReturnType> => {
	const { path, data, headers, returnRaw, withChannel } = payload;
	const _path = path instanceof URL ? path.href : await parsePath(path);

	try {
		const res = await fetch(_path, deleteParams(data, headers, withChannel));

		if (returnRaw) return res as unknown as ReturnType;

		return await returnJson<ReturnType>(res);
	} catch (e: unknown) {
		if (e instanceof FetchError) {
			throw e;
		} else {
			throw new FetchError({ error: 'Unknown error occurred', status: 500 });
		}
	}
};


export function getToken() {
	const state = store.getState();
	return state.AuthReducer.token ?? "";
}
